/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2021 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 *
 * @file SearchComponent.jsx
 * @author Srinivasan Bakthavatchalam
 * @since 1.0.1
 */

import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import InputAdornment from '@material-ui/core/InputAdornment';
import { Icon } from '@greenville/mui-theme';
import { withStyles } from '@material-ui/core/styles';
import { injectIntl, intlShape } from 'react-intl';

import ResourceUtils from '../../utils/ResourceUtils';
import ScrollContainer from '../common/ScrollContainer';
import StyledSearchField from './StyledSearchField';
import messages from '../../../translations/defaultMessages';

const styles = (theme) => ({
  searchIcon: {
    color: theme.color.activePrimary
  },
  searchTags: {
    height: 50
  },
  searchTagContainer: {
    display: 'flex',
    gap: '20px 10px',
    margin: '10px 0px'
  },
  searchTag: {
    alignItems: 'center',
    background: theme.color.background,
    border: `1px solid ${theme.color.divider}`,
    borderRadius: 6,
    color: theme.color.textPrimary,
    display: 'flex',
    flexDirection: 'row',
    fontFamily: 'Hind',
    fontSize: 16,
    fontStyle: 'normal',
    fontWeight: 500,
    height: 30,
    justifyContent: 'center',
    minWidth: 'fit-content',
    padding: '4px 8px 2px'
  }
});

const SearchComponent = ({
  bulkDownloadStatus,
  classes,
  filteredResourceData,
  intl,
  isCurrentTabEmpty,
  onKeywordChange,
  searchedKeyword
}) => {
  const searchedContentType = Object.keys(filteredResourceData);
  const { formatMessage } = intl;

  return (
    <Fragment>
      <StyledSearchField
        autoFocus
        disabled={isCurrentTabEmpty || ResourceUtils.isBulkDownloadInProgress(bulkDownloadStatus)}
        fullWidth
        id="resource-keyword-search"
        onChange={onKeywordChange}
        placeholder={formatMessage(messages.searchForAResource)}
        startAdornment={
          <InputAdornment className={classes.searchIcon} position="start">
            <Icon
              color="inherit"
              name="Search"
              size="small"
            />
          </InputAdornment>
        }
        value={searchedKeyword}
      />
      {filteredResourceData
        && searchedContentType.length > 0
        &&
        <div className={classes.searchTags}>
          <ScrollContainer>
            <div className={classes.searchTagContainer}>
              {searchedContentType.map((resource, index) => (
                <div
                  className={classes.searchTag}
                  key={index}
                >
                  {`${formatMessage(messages[resource.split(' ').join('_').toLowerCase()])} (${filteredResourceData[resource].length})`}
                </div>
              ))}
            </div>
          </ScrollContainer>
        </div>
      }
    </Fragment>);
};

SearchComponent.propTypes = {
  bulkDownloadStatus: PropTypes.string.isRequired,
  classes: PropTypes.object.isRequired,
  filteredResourceData: PropTypes.array.isRequired,
  intl: intlShape,
  isCurrentTabEmpty: PropTypes.bool.isRequired,
  onKeywordChange: PropTypes.func.isRequired,
  searchedKeyword: PropTypes.string
};

SearchComponent.defaultProps = {
  intl: {},
  searchedKeyword: ''
};

export default withStyles(styles)(injectIntl(SearchComponent));
